import React from "react"
import type { JSX } from "react"
import { useTranslation } from "react-i18next"

import { HStack } from "@/nzds/layout"
import { debounce } from "lodash-es"
import { DateTime, Interval } from "luxon"

import { Box, useTheme } from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro"
import type {
  DateValidationError,
  PickerChangeHandlerContext,
} from "@mui/x-date-pickers-pro"
import { AdapterLuxon } from "@mui/x-date-pickers-pro/AdapterLuxon"

import { useCallbackRef } from "../../utils/use-callback-ref"
import type { MonthRangePickerProps } from "./month-range-picker.types"

export const MonthRangePicker = ({
  availableMaxMinMonths,
  value,
  onChange,
  disabled,
  error,
  errorMessage,
  fullWidth,
}: MonthRangePickerProps): JSX.Element => {
  const theme = useTheme()
  const { i18n } = useTranslation()

  const stableOnChange = useCallbackRef(onChange)
  const debouncedOnChange = React.useMemo(
    () => debounce(stableOnChange, 250),
    [stableOnChange]
  )

  React.useEffect(() => {
    return () => {
      debouncedOnChange.cancel()
    }
  }, [debouncedOnChange])

  const isDisabled: boolean = React.useMemo(
    () => disabled ?? !availableMaxMinMonths?.isValid,
    [availableMaxMinMonths, disabled]
  )

  const handleStartChange = React.useCallback(
    (
      date: DateTime | null,
      context: PickerChangeHandlerContext<DateValidationError>
    ) => {
      debouncedOnChange(
        Interval.fromDateTimes(
          date?.startOf("month") ?? DateTime.invalid("Invalid date"),
          value?.end ?? DateTime.invalid("Invalid date")
        ),
        context
      )
    },
    [debouncedOnChange, value]
  )

  const handleEndChange = React.useCallback(
    (
      date: DateTime | null,
      context: PickerChangeHandlerContext<DateValidationError>
    ) => {
      debouncedOnChange(
        Interval.fromDateTimes(
          value?.start ?? DateTime.invalid("Invalid date"),
          date?.endOf("month") ?? DateTime.invalid("Invalid date")
        ),
        context
      )
    },
    [debouncedOnChange, value]
  )

  return (
    <HStack
      data-e2e="month-range-picker"
      data-testid="month-range-picker"
      sx={{
        alignItems: "baseline",
        justifyContent: "space-between",
        minWidth: theme.typography.pxToRem(408),
        width: fullWidth ? "100%" : theme.typography.pxToRem(408),
      }}
    >
      <LocalizationProvider
        adapterLocale={i18n.language}
        dateAdapter={AdapterLuxon}
      >
        <DatePicker
          disabled={isDisabled}
          maxDate={
            value?.end ??
            availableMaxMinMonths?.end ??
            DateTime.invalid("Invalid date")
          }
          minDate={
            availableMaxMinMonths?.start ?? DateTime.invalid("Invalid date")
          }
          onChange={handleStartChange}
          slotProps={{
            textField: {
              size: "small",
              error,
              slotProps: {
                htmlInput: { "aria-label": "Start month" },
              },
            },
            openPickerButton: { size: "small" },
            inputAdornment: { position: "end" },
          }}
          sx={{ width: theme.typography.pxToRem(184) }}
          value={value?.start}
          views={["year", "month"]}
        />
        <Box
          component="span"
          sx={{
            mx: 1,
          }}
        >
          &ndash;
        </Box>
        <DatePicker
          disabled={isDisabled}
          maxDate={
            availableMaxMinMonths?.end ?? DateTime.invalid("Invalid date")
          }
          minDate={
            value?.start ??
            availableMaxMinMonths?.start ??
            DateTime.invalid("Invalid date")
          }
          onChange={handleEndChange}
          slotProps={{
            openPickerButton: { size: "small" },
            inputAdornment: { position: "end" },
            textField: {
              error,
              size: "small",
              slotProps: {
                htmlInput: { "aria-label": "End month" },
              },
              helperText:
                error && errorMessage && !isDisabled ? errorMessage : "",
            },
          }}
          sx={{ width: theme.typography.pxToRem(184) }}
          value={value?.end}
          views={["year", "month"]}
        />
      </LocalizationProvider>
    </HStack>
  )
}
